import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PhoneCall, Mail, MessageSquare, Shield,
  Wifi, Clock, Zap, ArrowRight, Calculator
} from 'lucide-react';
import useComparisonStore from './ComparisonModals/store/comparisonStore';

const Footer = () => {
  const navigate = useNavigate();
  
  // Handle recommendation button click
  const handleRecommendClick = (e) => {
    e.preventDefault();
    
    // Get the functions directly from the store
    const { closeAllModals, openRecommendationModal } = useComparisonStore.getState();
    
    // First close all modals to ensure a clean state
    closeAllModals();
    
    // Then open the recommendation modal
    openRecommendationModal();
  };
  
  return (
    <footer className="w-full font-sans bg-black text-white">
      {/* Recommendation Section - Visually distinct from the footer */}
      <div className="w-full bg-zinc-900 py-8">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-zinc-800 p-6 rounded-lg border-l-4 border-white">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-white">
                <Calculator className="w-5 h-5 text-black" />
              </div>
              <div>
                <h3 className="text-white font-medium text-base">Not sure which package is right for you?</h3>
                <p className="text-zinc-400 mt-1 text-sm">Get a personalized recommendation based on your needs</p>
              </div>
            </div>
            <button
              onClick={handleRecommendClick}
              className="px-6 py-2.5 bg-white text-black rounded-md hover:bg-zinc-200 transition-all whitespace-nowrap flex items-center gap-2 text-sm font-medium"
            >
              Get Recommendation
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="w-full border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            {/* Column 1 - Company Info */}
            <div className="md:col-span-5">
              <img 
                src="https://apextech.group/wondernet-logo-white.png"
                alt="Wondernet Logo" 
                className="h-8 mb-6"
              />
              <p className="text-zinc-400 max-w-md leading-relaxed text-sm mb-10">
                Enterprise-grade connectivity for your home. Experience the difference with our premium network infrastructure and dedicated support.
              </p>
              
              {/* Features highlight */}
              <div>
                <h4 className="text-white font-medium text-xs uppercase tracking-wider mb-5">Key Features</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-4 h-4 text-zinc-500" />
                    <span className="text-zinc-300 text-sm">Enterprise-Grade Network</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-zinc-500" />
                    <span className="text-zinc-300 text-sm">Expert Support</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Wifi className="w-4 h-4 text-zinc-500" />
                    <span className="text-zinc-300 text-sm">Free Premium Router</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Zap className="w-4 h-4 text-zinc-500" />
                    <span className="text-zinc-300 text-sm">Zero Throttling</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2 - Quick Links */}
            <div className="md:col-span-3 md:col-start-7">
              <h4 className="text-white font-medium text-xs uppercase tracking-wider mb-5">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="#"
                    className="text-zinc-400 text-sm hover:text-white transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      
                      // Get the functions directly from the store
                      const { closeAllModals, openSelectionModal } = useComparisonStore.getState();
                      
                      // First close all modals to ensure a clean state
                      closeAllModals();
                      
                      // Then open the selection modal with all operators
                      setTimeout(() => {
                        openSelectionModal({
                          selectAllOperators: true
                        });
                      }, 300);
                    }}
                  >
                    Packages
                  </a>
                </li>
                <li>
                  <Link to="/coverage" className="text-zinc-400 text-sm hover:text-white transition-colors">
                    Coverage
                  </Link>
                </li>
                <li>
                  <Link to="/wonderwatch" className="text-zinc-400 text-sm hover:text-white transition-colors">
                    WonderWatch™
                  </Link>
                </li>
                <li>
                  <Link to="/contact-us" className="text-zinc-400 text-sm hover:text-white transition-colors">
                    Support
                  </Link>
                </li>
                <li>
                  <Link to="/switch" className="text-zinc-400 text-sm hover:text-white transition-colors">
                    How to Switch
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 3 - Contact */}
            <div className="md:col-span-3 md:col-start-10">
              <h4 className="text-white font-medium text-xs uppercase tracking-wider mb-5">Contact</h4>
              <ul className="space-y-6">
                <li>
                  <a 
                    href="https://wa.me/27600672028" 
                    className="flex items-center text-zinc-400 hover:text-white transition-colors"
                  >
                    <MessageSquare className="w-5 h-5 mr-3 text-zinc-500" />
                    <div>
                      <span className="block text-sm text-zinc-300">WhatsApp Support</span>
                      <span className="block text-xs text-zinc-500 mt-0.5">Quick Responses</span>
                    </div>
                  </a>
                </li>
                <li>
                  <button className="flex items-center text-zinc-400 hover:text-white transition-colors w-full text-left">
                    <PhoneCall className="w-5 h-5 mr-3 text-zinc-500" />
                    <div>
                      <span className="block text-sm text-zinc-300">Live Chat</span>
                      <span className="block text-xs text-zinc-500 mt-0.5">Real-time assistance</span>
                    </div>
                  </button>
                </li>
                <li>
                  <a 
                    href="mailto:support@wondernet.co.za" 
                    className="flex items-center text-zinc-400 hover:text-white transition-colors"
                  >
                    <Mail className="w-5 h-5 mr-3 text-zinc-500" />
                    <div>
                      <span className="block text-sm text-zinc-300">Email Support</span>
                      <span className="block text-xs text-zinc-500 mt-0.5">support@wondernet.co.za</span>
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="w-full py-6">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-zinc-500 text-xs">
              © 2025 Wondernet. All rights reserved.
            </div>
            
            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              <Link to="/terms-and-conditions" className="text-zinc-500 text-xs hover:text-white transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy-policy" className="text-zinc-500 text-xs hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/acceptable-use-policy" className="text-zinc-500 text-xs hover:text-white transition-colors">
                Acceptable Use Policy
              </Link>
            </div>
            
            <div className="flex items-center gap-6">
              <a href="#" className="text-zinc-500 hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href="#" className="text-zinc-500 hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
              <a href="#" className="text-zinc-500 hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                </svg>
              </a>
              <a href="#" className="text-zinc-500 hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;